import { useRef, useEffect, useCallback } from 'react';
import jspreadsheet, { JspreadsheetInstance, Column, CellValue } from 'jspreadsheet-ce';
import 'jspreadsheet-ce/dist/jspreadsheet.css';
import 'jsuites/dist/jsuites.css';

export interface SpreadsheetColumn {
  title: string;
  width: number;
  type?: 'text' | 'numeric' | 'dropdown' | 'calendar' | 'checkbox' | 'html';
  source?: string[];
  readOnly?: boolean;
  align?: 'left' | 'center' | 'right';
  mask?: string;
  decimal?: string;
}

export interface SpreadsheetMeta {
  row: number;
  hasEdits?: boolean;
  fileId?: number;
  externalId?: string;
  transactionId?: number;
  [key: string]: unknown;
}

export interface CellChangeEvent {
  row: number;
  col: number;
  oldValue: CellValue;
  newValue: CellValue;
  meta?: SpreadsheetMeta;
}

interface SpreadsheetEditorProps {
  data: (string | number | null)[][];
  columns: SpreadsheetColumn[];
  metadata?: SpreadsheetMeta[];
  onCellChange?: (event: CellChangeEvent) => void;
  onApprove?: (row: number, approved: boolean) => void;
  height?: string;
  minDimensions?: [number, number];
  allowInsertRow?: boolean;
  allowDeleteRow?: boolean;
  editable?: boolean;
  highlightEdited?: boolean;
  approveColumn?: number;
}

export default function SpreadsheetEditor({
  data,
  columns,
  metadata = [],
  onCellChange,
  onApprove,
  height = '100%',
  minDimensions = [10, 20],
  allowInsertRow = false,
  allowDeleteRow = false,
  editable = true,
  highlightEdited = true,
  approveColumn = 0,
}: SpreadsheetEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const spreadsheetRef = useRef<JspreadsheetInstance | null>(null);
  const dataRef = useRef(data);
  const metadataRef = useRef(metadata);

  // Update refs when props change
  dataRef.current = data;
  metadataRef.current = metadata;

  const handleCellChange = useCallback(
    (
      _instance: HTMLElement,
      _cell: HTMLTableCellElement,
      x: string | number,
      y: string | number,
      value: CellValue
    ) => {
      if (!onCellChange) return;

      const colIndex = typeof x === 'string' ? parseInt(x, 10) : x;
      const rowIndex = typeof y === 'string' ? parseInt(y, 10) : y;
      const oldData = dataRef.current[rowIndex];
      const oldValue = oldData ? oldData[colIndex] : null;
      const meta = metadataRef.current[rowIndex];

      // Check if this is the approve column button click
      if (colIndex === approveColumn && onApprove) {
        const currentValue = value as string;
        const isApproved = currentValue === 'Approved';
        onApprove(rowIndex, !isApproved);
        return;
      }

      onCellChange({
        row: rowIndex,
        col: colIndex,
        oldValue: oldValue as CellValue,
        newValue: value,
        meta,
      });
    },
    [onCellChange, onApprove, approveColumn]
  );

  const handleBeforeChange = useCallback(
    (
      _instance: HTMLElement,
      _cell: HTMLTableCellElement,
      x: string | number,
      y: string | number,
      value: CellValue
    ) => {
      const colIndex = typeof x === 'string' ? parseInt(x, 10) : x;

      // For approve column, handle as button click
      if (colIndex === approveColumn && onApprove) {
        const rowIndex = typeof y === 'string' ? parseInt(y, 10) : y;
        const currentValue = dataRef.current[rowIndex]?.[colIndex] as string;
        const isApproved = currentValue === 'Approved';
        onApprove(rowIndex, !isApproved);
        return false; // Prevent default change
      }

      return value;
    },
    [onApprove, approveColumn]
  );

  useEffect(() => {
    if (!containerRef.current) return;

    // Convert columns to jSpreadsheet format
    const jColumns: Column[] = columns.map((col) => ({
      title: col.title,
      width: col.width,
      type: col.type || 'text',
      source: col.source,
      readOnly: col.readOnly,
      align: col.align,
      mask: col.mask,
      decimal: col.decimal,
    }));

    // Prepare data with proper formatting
    const preparedData = data.length > 0 ? data : [[]];

    // Initialize jSpreadsheet
    spreadsheetRef.current = jspreadsheet(containerRef.current, {
      data: preparedData,
      columns: jColumns,
      minDimensions,
      allowInsertRow,
      allowDeleteRow,
      allowInsertColumn: false,
      allowDeleteColumn: false,
      tableOverflow: true,
      tableWidth: '100%',
      tableHeight: height,
      freezeColumns: 1,
      defaultColWidth: 100,
      onchange: handleCellChange,
      onbeforechange: handleBeforeChange,
      contextMenu: () => false,
      updateTable: (
        _instance: JspreadsheetInstance,
        cell: HTMLTableCellElement,
        col: number,
        row: number,
        value: CellValue
      ) => {
        // Highlight rows with edits
        if (highlightEdited && metadata[row]?.hasEdits) {
          cell.style.backgroundColor = '#fef3c7'; // yellow-100
        }

        // Style approve column as buttons
        if (col === approveColumn) {
          const status = value as string;
          cell.innerHTML = '';
          const btn = document.createElement('button');
          btn.className = getApproveButtonClass(status);
          btn.textContent = status || 'Approve';
          btn.onclick = (e) => {
            e.stopPropagation();
            if (onApprove && status !== 'N/A') {
              onApprove(row, status !== 'Approved');
            }
          };
          cell.appendChild(btn);
          cell.style.padding = '4px';
        }
      },
    });

    // Apply read-only styling
    if (!editable) {
      containerRef.current.querySelectorAll('td').forEach((cell) => {
        cell.style.cursor = 'default';
      });
    }

    return () => {
      if (spreadsheetRef.current && containerRef.current) {
        jspreadsheet.destroy(containerRef.current);
        spreadsheetRef.current = null;
      }
    };
  }, []);

  // Update data when it changes
  useEffect(() => {
    if (spreadsheetRef.current && data.length > 0) {
      spreadsheetRef.current.setData(data);
    }
  }, [data]);

  return (
    <div
      ref={containerRef}
      className="spreadsheet-container"
      style={{ height, overflow: 'auto' }}
    />
  );
}

function getApproveButtonClass(status: string): string {
  const baseClass = 'px-2 py-1 text-xs font-medium rounded cursor-pointer';
  switch (status) {
    case 'Approved':
      return `${baseClass} bg-green-500 text-white`;
    case 'Removed':
      return `${baseClass} bg-red-500 text-white`;
    case 'Issue':
      return `${baseClass} bg-yellow-500 text-white`;
    case 'N/A':
      return `${baseClass} bg-gray-300 text-gray-600 cursor-not-allowed`;
    default:
      return `${baseClass} bg-blue-100 text-blue-800 hover:bg-blue-200`;
  }
}
